import React, { useState } from 'react';
import { ArrowRight, Mail, Phone } from 'lucide-react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', revenue: '', industry: '', message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = new URLSearchParams({
        'form-name': 'contact',
        name: formData.name,
        email: formData.email,
        company: formData.company || '',
        revenue: formData.revenue || '',
        industry: formData.industry || '',
        message: formData.message,
      }).toString();
      const response = await fetch('/__forms.html', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error();
      }
    } catch {
      setError('Something went wrong. Please email us directly at Hello@Zahela.ca');
    }
    setLoading(false);
  };

  return (
    <div className="page">

      <section style={{ paddingBottom: 64, borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div className="red-line" />
          <span className="eyebrow">Start the Conversation</span>
          <h1 style={{ fontSize: 'clamp(40px, 5vw, 72px)', maxWidth: 640 }}>
            Let's talk growth.
          </h1>
          <p className="lead" style={{ marginTop: 24 }}>
            Whether you're a business owner ready to grow, a sales leader who needs better systems, or a founder who wants AI working for you — we want to hear from you.
          </p>
        </div>
      </section>

      <section>
        <div className="container">
          <div className="contact-grid">

            {/* Left: Info */}
            <div>
              <div className="red-line" />
              <span className="eyebrow">Contact Info</span>
              <div style={{ marginTop: 40 }}>
                <div className="contact-info-item">
                  <div className="contact-info-label">Email</div>
                  <div className="contact-info-value">
                    <a href="mailto:Hello@Zahela.ca" style={{ color: 'var(--white)', textDecoration: 'none' }}>Hello@Zahela.ca</a>
                  </div>
                </div>
                <div className="contact-info-item">
                  <div className="contact-info-label">Response Time</div>
                  <div className="contact-info-value">Within 24 hours</div>
                </div>
                <div className="contact-info-item">
                  <div className="contact-info-label">Based In</div>
                  <div className="contact-info-value">Canada — Serving North America</div>
                </div>
              </div>

              <div style={{ marginTop: 48, padding: '32px', background: 'var(--black-card)', border: '1px solid var(--border)' }}>
                <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--red)', marginBottom: 16 }}>What to Expect</p>
                <ul className="service-list">
                  <li>30-minute discovery call</li>
                  <li>Honest assessment of fit</li>
                  <li>Custom strategy overview</li>
                  <li>Clear next steps — no pressure</li>
                </ul>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              {!submitted ? (
                <>
                  <div className="red-line" />
                  <span className="eyebrow">Send a Message</span>
                  <form
                    name="contact"
                    method="POST"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                    style={{ marginTop: 40 }}
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <p style={{ display: 'none' }}><input name="bot-field" /></p>
                    {error && (
                      <p style={{ color: '#e74c3c', fontSize: 13, marginBottom: 20, padding: '12px 16px', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)' }}>
                        {error}
                      </p>
                    )}
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input className="form-input" type="text" placeholder="Your name" value={formData.name} onChange={e => handleChange('name', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email Address *</label>
                        <input className="form-input" type="email" placeholder="Your email" value={formData.email} onChange={e => handleChange('email', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">Company Name</label>
                        <input className="form-input" type="text" placeholder="Your company" value={formData.company} onChange={e => handleChange('company', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Annual Revenue</label>
                        <select className="form-select" value={formData.revenue} onChange={e => handleChange('revenue', e.target.value)}>
                          <option value="">Select range</option>
                          <option value="under-500k">Under $500K</option>
                          <option value="500k-1m">$500K – $1M</option>
                          <option value="1m-5m">$1M – $5M</option>
                          <option value="5m-10m">$5M – $10M</option>
                          <option value="10m-25m">$10M – $25M</option>
                          <option value="25m+">$25M+</option>
                          <option value="undisclosed">Prefer not to say</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Industry</label>
                      <input className="form-input" type="text" placeholder="e.g. SaaS, Manufacturing, Consulting" value={formData.industry} onChange={e => handleChange('industry', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Message *</label>
                      <textarea className="form-textarea" placeholder="Tell us about your business and what you're trying to solve." value={formData.message} onChange={e => handleChange('message', e.target.value)} required />
                    </div>
                    <button className="btn-red" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
                      {loading ? 'Sending...' : 'Send Message'} <ArrowRight size={14} />
                    </button>
                  </form>
                </>
              ) : (
                <div className="success-box">
                  <Mail size={40} style={{ color: 'var(--red)', marginBottom: 24 }} />
                  <h3 style={{ marginBottom: 12 }}>Message received.</h3>
                  <p style={{ color: 'var(--gray-mid)', fontSize: 15 }}>
                    We'll get back to you within 24 hours. In the meantime, feel free to email us directly at{' '}
                    <a href="mailto:Hello@Zahela.ca" style={{ color: 'var(--red)' }}>Hello@Zahela.ca</a>.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
